import React, { useEffect, useRef, useState } from 'react';
import PePapersDisclaimerGate from './shared/PePapersDisclaimerGate';
import { createPortal } from 'react-dom';
import { Check, FileText, RotateCcw, Scissors, ShieldCheck, X } from 'lucide-react';
import { savePePapersArchiveDoc } from '../../services/pePapersArchiveService';
import {
    usePEPapersStore,
    type EquipmentRulesConfig,
    type SocialMediaRulesConfig,
} from '../../store/usePEPapersStore';
import { usePersonalDataSelector } from '../../store/usePersonalDataSelector';
import useAppStore from '../../store/appStore';
import { assembleInstructionSections } from '../../utils/pe-papers/instructionAssembler';
import { buildHtmlAsPdfBlob, downloadHtmlAsPdf } from '../../utils/pe-papers/htmlDocumentPdfDownload';

export interface PEPapersInstructionModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData?: any;
    onArchived?: () => void;
}

interface BetriebFormData {
    firmenname: string;
    strasse: string;
    plz: string;
    ort: string;
    inhaber: string;
}

const DA_CONTRACT_PDF_CSS = `
  .av-a4-page {
    display: block !important;
    width: 100% !important;
    max-width: 210mm !important;
    margin-left: auto !important;
    margin-right: auto !important;
    box-sizing: border-box !important;
    font-family: Georgia, 'Times New Roman', serif !important;
    color: #0f172a !important;
    font-size: 12.5px !important;
    line-height: 1.42 !important;
    padding: 9mm 12mm 16mm 12mm !important;
    background: #ffffff !important;
  }
  .av-a4-page * { box-sizing: border-box !important; }
  .av-a4-page .pdf-letterhead {
    width: 100% !important;
    text-align: center !important;
    border-bottom: 2px solid #1e293b !important;
    margin: 0 0 7px !important;
    padding: 0 0 6px !important;
  }
  .av-a4-page .pdf-letterhead h1 {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 15px !important;
    font-weight: 900 !important;
    letter-spacing: 0.04em !important;
    text-transform: uppercase !important;
    color: #0f172a !important;
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 0 3px !important;
    text-align: center !important;
  }
  .av-a4-page .pdf-letterhead h2 {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 12.5px !important;
    font-weight: 700 !important;
    letter-spacing: 0.14em !important;
    text-transform: uppercase !important;
    color: #b48a56 !important;
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 !important;
    text-align: center !important;
  }
  .av-meta-grid {
    display: table !important;
    width: 100% !important;
    table-layout: fixed !important;
    margin: 0 0 7px !important;
    border: 1px solid #cbd5e1 !important;
    background: #f8fafc !important;
  }
  .av-meta-col {
    display: table-cell !important;
    width: 50% !important;
    vertical-align: top !important;
    padding: 7px 9px !important;
    font-size: 12.5px !important;
    line-height: 1.4 !important;
    color: #334155 !important;
    text-align: left !important;
  }
  .av-meta-col + .av-meta-col { border-left: 1px solid #e2e8f0 !important; }
  .av-meta-label {
    display: block !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.12em !important;
    color: #64748b !important;
    margin: 0 0 4px !important;
  }
  .av-meta-col strong { color: #0f172a !important; font-weight: 700 !important; }
  .av-meta-col p {
    width: 100% !important;
    margin: 0 !important;
    text-align: left !important;
    font-size: 12px !important;
    line-height: 1.4 !important;
    color: #334155 !important;
  }
  .av-meta-col .av-meta-date {
    display: block !important;
    margin-top: 4px !important;
    color: #64748b !important;
    font-size: 11px !important;
  }
  .av-doc-section {
    display: block !important;
    width: 100% !important;
    margin: 0 0 5px !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }
  .av-a4-page h3,
  .av-doc-section > h3 {
    display: block !important;
    width: 100% !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.05em !important;
    text-align: left !important;
    background: #1e293b !important;
    color: #f8fafc !important;
    border: none !important;
    border-radius: 0 !important;
    padding: 5px 9px !important;
    margin: 0 0 5px !important;
  }
  .av-a4-page p,
  .av-doc-p,
  .av-doc-section p {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    font-family: Georgia, 'Times New Roman', serif !important;
    font-size: 12.5px !important;
    line-height: 1.42 !important;
    color: #1e293b !important;
    text-align: justify !important;
    margin: 0 0 3px !important;
  }
  .av-sig-grid {
    display: table !important;
    width: 100% !important;
    table-layout: fixed !important;
    margin-top: 10px !important;
  }
  .av-sig-col {
    display: table-cell !important;
    width: 50% !important;
    vertical-align: top !important;
    padding: 0 10px 0 0 !important;
  }
  .av-sig-col + .av-sig-col { padding: 0 0 0 10px !important; }
  .av-sig-line {
    display: block !important;
    width: 200px !important;
    max-width: 100% !important;
    height: 0 !important;
    border: none !important;
    border-bottom: 2px solid #1e293b !important;
    margin: 18px 0 4px !important;
  }
  .av-sig-label {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 12px !important;
    color: #64748b !important;
    margin: 0 !important;
    text-align: left !important;
  }
  .av-sig-name {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 12px !important;
    font-weight: 700 !important;
    color: #0f172a !important;
    margin: 0 0 2px !important;
    text-align: left !important;
  }
  .av-table-wrap {
    display: block !important;
    width: 100% !important;
    margin: 0 0 4px !important;
    page-break-inside: auto !important;
    break-inside: auto !important;
  }
  .av-table-wrap table {
    width: 100% !important;
    border-collapse: collapse !important;
    table-layout: fixed !important;
    font-size: 12.5px !important;
    page-break-inside: auto !important;
    break-inside: auto !important;
  }
  .av-table-wrap thead th,
  .av-table-wrap th {
    background-color: #f1f5f9 !important;
    color: #0f172a !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 11px !important;
    font-weight: 600 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.05em !important;
    padding: 5px 6px !important;
    border: 1px solid #64748b !important;
    border-bottom: 1.5px solid #334155 !important;
    text-align: left !important;
  }
  .av-table-wrap td {
    padding: 8px 8px !important;
    border: 1px solid #64748b !important;
    color: #334155 !important;
    text-align: left !important;
    vertical-align: middle !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 12.5px !important;
    height: 32px !important;
    min-height: 32px !important;
  }
  .av-table-wrap tbody tr.signature-row td {
    height: 32px !important;
    min-height: 32px !important;
    padding: 8px 8px !important;
    border-color: #475569 !important;
  }
  .av-table-wrap tbody tr.signature-row {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }
  .print-break,
  .pepapers-print-break {
    margin-top: 24px !important;
    page-break-before: always !important;
    break-before: page !important;
  }
  @media print {
    @page { size: A4; margin: 0; }
    body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
    .av-a4-page {
      box-shadow: none !important;
      margin: 0 !important;
      max-width: none !important;
      width: 100% !important;
      min-height: 0 !important;
    }
  }
`;

const A4_PREVIEW_PAGE_CLASS =
    'av-a4-page bg-white text-slate-900 my-4 mx-auto w-full max-w-[210mm] h-auto min-h-0 shadow-xl rounded-sm text-left block overflow-visible print:my-0 print:shadow-none';

const DOC_P_CLASS = 'av-doc-p mb-1 text-justify text-slate-800 leading-snug';

const INPUT_CLASS =
    'w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black text-pe-text dark:text-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#E6C299]/50 transition-colors';

const LABEL_CLASS = 'text-xs font-semibold text-pe-muted dark:text-slate-400 uppercase tracking-wider mb-1 block';

const STEP_LABELS: Record<number, string> = {
    1: 'Konfiguration',
    2: 'Vorschau & Archiv',
};

const SOCIAL_TOGGLES: Array<{
    key: keyof SocialMediaRulesConfig;
    title: string;
    sub: string;
}> = [
    {
        key: 'noPrivatePhoneAtWork',
        title: 'Kein privates Smartphone am Arbeitsplatz',
        sub: 'Private Nutzung während der Bedienzeit am Kunden untersagen.',
    },
    {
        key: 'noPrivateCustomerPosts',
        title: 'Verbot privater Kunden-Posts',
        sub: 'Kundenfotos dürfen nicht auf privaten Mitarbeiter-Profilen veröffentlicht werden.',
    },
    {
        key: 'onlyOfficialAccountsAllowed',
        title: 'Nur offizielle Salon-Kanäle',
        sub: 'Salon-Content und Vorher/Nachher-Bilder exklusiv über Salon-Accounts ausspielen.',
    },
    {
        key: 'strictDataPrivacyNoDMs',
        title: 'DSGVO-Schutz (Keine Beratung via DM)',
        sub: 'Terminabsprachen und Beratungen dürfen nicht über private Direktnachrichten erfolgen.',
    },
];

const EQUIPMENT_TOGGLES: Array<{
    key: keyof EquipmentRulesConfig;
    title: string;
    sub: string;
}> = [
    {
        key: 'ipadBusinessOnly',
        title: 'Salon-Hardware nur geschäftlich',
        sub: 'iPads und Terminals dürfen nicht für private Downloads/Surfen genutzt werden.',
    },
    {
        key: 'noToolsHomeTaking',
        title: 'Mitnahmeverbot für Salon-Werkzeuge',
        sub: 'Nur Salon-Eigentum bleibt im Betrieb; privat mitgebrachtes Werkzeug ist ausgenommen.',
    },
    {
        key: 'careAndLiabilityDuty',
        title: 'Sorgfaltspflicht & Schadenshaftung',
        sub: 'Pflicht zur pfleglichen Behandlung; Haftung bei grober Fahrlässigkeit.',
    },
    {
        key: 'returnOnTermination',
        title: 'Rückgabepflicht bei Ausscheiden',
        sub: 'Sämtliches überlassenes Equipment ist am letzten Arbeitstag vollständig abzugeben.',
    },
];

const DEFAULT_SOCIAL: SocialMediaRulesConfig = {
    noPrivatePhoneAtWork: true,
    noPrivateCustomerPosts: true,
    onlyOfficialAccountsAllowed: true,
    strictDataPrivacyNoDMs: true,
};

const DEFAULT_EQUIPMENT: EquipmentRulesConfig = {
    ipadBusinessOnly: true,
    noToolsHomeTaking: true,
    careAndLiabilityDuty: true,
    returnOnTermination: true,
};

const isEmpty = (v: unknown) => !String(v ?? '').trim();

const formatDateGerman = (iso: string): string => {
    if (!iso) return '____________';
    const parts = iso.split('-');
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
    try {
        return new Date(iso).toLocaleDateString('de-DE');
    } catch {
        return iso;
    }
};

const formatTodayGerman = (): string =>
    new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

const buildBetriebFromClient = (clientData: any): BetriebFormData => ({
    firmenname: clientData?.salonname || clientData?.firmenname || '',
    strasse: clientData?.strasse || '',
    plz: clientData?.plz || '',
    ort: clientData?.ort || '',
    inhaber: clientData?.unternehmername || '',
});

const ToggleRow: React.FC<{
    title: string;
    sub: string;
    checked: boolean;
    onChange: (value: boolean) => void;
}> = ({ title, sub, checked, onChange }) => (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-[#0a0a0a] px-4 py-3">
        <div className="min-w-0">
            <p className="text-sm font-semibold text-pe-text dark:text-slate-50">{title}</p>
            <p className="mt-0.5 text-xs leading-relaxed text-pe-muted dark:text-slate-400">{sub}</p>
        </div>
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            onClick={() => onChange(!checked)}
            className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors ${
                checked ? 'bg-[#E6C299]' : 'bg-slate-600'
            }`}
        >
            <span
                className={`absolute top-0.5 left-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-white shadow transition-transform ${
                    checked ? 'translate-x-5' : 'translate-x-0'
                }`}
            >
                {checked ? <Check className="h-3 w-3 text-[#b48a56]" /> : null}
            </span>
        </button>
    </div>
);

const PEPapersInstructionModal: React.FC<PEPapersInstructionModalProps> = ({
    isOpen,
    onClose,
    initialData,
    onArchived,
}) => {
    const store = usePEPapersStore();
    const { personalData } = usePersonalDataSelector();
    const clientData = useAppStore((s: any) => s.clientData);

    const [currentStep, setCurrentStep] = useState(1);
    const [betriebData, setBetriebData] = useState<BetriebFormData>({
        firmenname: '',
        strasse: '',
        plz: '',
        ort: '',
        inhaber: '',
    });
    const [selectedEmployeeId, setSelectedEmployeeId] = useState('all');
    const [effectiveDate, setEffectiveDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [customRulesText, setCustomRulesText] = useState('');
    const [socialMedia, setSocialMedia] = useState<SocialMediaRulesConfig>(DEFAULT_SOCIAL);
    const [equipment, setEquipment] = useState<EquipmentRulesConfig>(DEFAULT_EQUIPMENT);
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isPdfDownloading, setIsPdfDownloading] = useState(false);
    const [archiveFeedback, setArchiveFeedback] = useState<'idle' | 'success' | 'error' | 'draft'>('idle');
    const [archiveDocId, setArchiveDocId] = useState<string | null>(null);

    const printContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) {
            setCurrentStep(1);
            setInvalidFields(new Set());
            setDisclaimerAccepted(false);
            setIsSaving(false);
            setArchiveFeedback('idle');
            setArchiveDocId(null);
            return;
        }

        const fromClient = buildBetriebFromClient(clientData);
        setBetriebData(fromClient);
        setSelectedEmployeeId(store.selectedEmployeeId || 'all');
        setEffectiveDate(store.effectiveDate || new Date().toISOString().split('T')[0]);
        setCustomRulesText(store.customRulesText || '');
        setSocialMedia({ ...DEFAULT_SOCIAL, ...store.socialMedia });
        setEquipment({ ...DEFAULT_EQUIPMENT, ...store.equipment });
        setCurrentStep(1);
        setInvalidFields(new Set());
        setDisclaimerAccepted(false);
        setIsSaving(false);
        setArchiveFeedback('idle');
        setArchiveDocId(initialData?.id || null);

        if (initialData) {
            if (initialData.betriebData && typeof initialData.betriebData === 'object') {
                setBetriebData({ ...fromClient, ...initialData.betriebData });
            }
            if (typeof initialData.selectedEmployeeId === 'string') {
                setSelectedEmployeeId(initialData.selectedEmployeeId);
            }
            if (typeof initialData.effectiveDate === 'string') {
                setEffectiveDate(initialData.effectiveDate);
            }
            if (typeof initialData.customRulesText === 'string') {
                setCustomRulesText(initialData.customRulesText);
            }
            if (initialData.socialMedia && typeof initialData.socialMedia === 'object') {
                setSocialMedia({ ...DEFAULT_SOCIAL, ...initialData.socialMedia });
            }
            if (initialData.equipment && typeof initialData.equipment === 'object') {
                setEquipment({ ...DEFAULT_EQUIPMENT, ...initialData.equipment });
            }
            const step = Number(initialData.currentStep);
            if (Number.isFinite(step) && step >= 1 && step <= 2) {
                setCurrentStep(step);
            }
            if (initialData.confirmedDisclaimer) {
                setDisclaimerAccepted(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate once per open
    }, [isOpen, clientData, initialData]);

    if (!isOpen) return null;

    const employees = (personalData || []).filter((emp: { id?: string }) => emp?.id);
    const selectedEmployee = employees.find((emp: { id: string }) => emp.id === selectedEmployeeId);
    const empName =
        selectedEmployeeId === 'all'
            ? 'Gesamtes Mitarbeiter-Team'
            : selectedEmployee?.name || 'Mitarbeiter';
    const signatureEmployeeName =
        selectedEmployeeId === 'all' || !selectedEmployee?.name?.trim()
            ? 'Name des Mitarbeiters (Druckbuchstaben)'
            : selectedEmployee.name.trim();
    const ortDatum = `${betriebData.ort || '____________'}, ${formatTodayGerman()}`;

    const {
        sections: documentSections,
        headerTitle: documentHeaderTitle,
        archiveLabel: documentArchiveLabel,
        pdfFilenameStem: documentPdfStem,
        subtitle: documentSubtitle,
    } = assembleInstructionSections({
        socialMedia,
        equipment,
        customRulesText,
    });
    const isTeamScope = selectedEmployeeId === 'all';
    const signatureRowCount = 16;

    const salonAddressLine = [betriebData.strasse, [betriebData.plz, betriebData.ort].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ');

    const clearFieldError = (field: string) => {
        setInvalidFields((prev) => {
            if (!prev.has(field)) return prev;
            const next = new Set(prev);
            next.delete(field);
            return next;
        });
    };

    const updateBetriebField = <K extends keyof BetriebFormData>(key: K, value: BetriebFormData[K]) => {
        setBetriebData((prev) => ({ ...prev, [key]: value }));
        clearFieldError(`betrieb_${key}`);
    };

    const validateStep1 = (): string[] => {
        const errors: string[] = [];
        if (isEmpty(betriebData.firmenname)) errors.push('betrieb_firmenname');
        if (isEmpty(betriebData.strasse)) errors.push('betrieb_strasse');
        if (isEmpty(betriebData.plz)) errors.push('betrieb_plz');
        if (isEmpty(betriebData.ort)) errors.push('betrieb_ort');
        if (isEmpty(effectiveDate)) errors.push('effectiveDate');
        return errors;
    };

    const isFieldInvalid = (field: string) => invalidFields.has(field);

    const getInputClass = (baseClass: string, field: string) =>
        isFieldInvalid(field) ? `${baseClass} border-red-500 focus:border-red-500` : baseClass;

    const syncStore = () => {
        store.setSelectedEmployeeId(selectedEmployeeId);
        store.setEffectiveDate(effectiveDate);
        store.setCustomRulesText(customRulesText);
        (Object.keys(socialMedia) as Array<keyof SocialMediaRulesConfig>).forEach((key) => {
            store.setSocialMediaFlag(key, socialMedia[key]);
        });
        (Object.keys(equipment) as Array<keyof EquipmentRulesConfig>).forEach((key) => {
            store.setEquipmentFlag(key, equipment[key]);
        });
    };

    const handleWeiter = () => {
        const errors = validateStep1();
        if (errors.length > 0) {
            setInvalidFields(new Set(errors));
            return;
        }
        setInvalidFields(new Set());
        syncStore();
        setCurrentStep(2);
    };

    const handleZurueck = () => {
        setInvalidFields(new Set());
        setCurrentStep(1);
    };

    const handleClose = () => {
        setCurrentStep(1);
        setInvalidFields(new Set());
        setDisclaimerAccepted(false);
        setIsSaving(false);
        setArchiveFeedback('idle');
        onClose();
    };

    const handleReset = () => {
        store.resetConfig();
        setSelectedEmployeeId('all');
        setEffectiveDate(new Date().toISOString().split('T')[0]);
        setCustomRulesText('');
        setSocialMedia({ ...DEFAULT_SOCIAL });
        setEquipment({ ...DEFAULT_EQUIPMENT });
        setBetriebData(buildBetriebFromClient(clientData));
    };

    const isActionLocked = !disclaimerAccepted;

    const getActionButtonClass = (baseClass: string) =>
        `${baseClass} ${isActionLocked ? 'opacity-50 cursor-not-allowed' : ''}`;

    const getDocumentHtml = () =>
        printContainerRef.current?.innerHTML ||
        document.getElementById('pepapers-dienstanweisung-print')?.innerHTML ||
        '';

    const buildPdfOptions = (html: string) => {
        const dateIso = new Date().toISOString().slice(0, 10);
        return {
            html,
            filename: `${documentPdfStem}_${dateIso}.pdf`,
            scrubMode: 'minimal' as const,
            splitPrintBreaks: true as const,
            capturePadPx: 0,
            letterheadOnBreaks: false,
            extraCss: DA_CONTRACT_PDF_CSS,
        };
    };

    const handlePrintIsolated = async () => {
        if (isActionLocked || isPdfDownloading) return;
        const html = getDocumentHtml();
        if (!html.trim()) {
            window.alert('Dokumentinhalt fehlt — bitte Vorschau prüfen.');
            return;
        }
        setIsPdfDownloading(true);
        try {
            const blob = await buildHtmlAsPdfBlob(buildPdfOptions(html));
            const url = URL.createObjectURL(blob);
            const iframe = document.createElement('iframe');
            iframe.setAttribute('title', 'Dienstanweisung Druck');
            iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
            document.body.appendChild(iframe);
            const cleanup = () => {
                window.setTimeout(() => {
                    iframe.remove();
                    URL.revokeObjectURL(url);
                }, 1500);
            };
            iframe.onload = () => {
                try {
                    iframe.contentWindow?.focus();
                    iframe.contentWindow?.print();
                } finally {
                    cleanup();
                }
            };
            iframe.src = url;
        } catch (err) {
            console.error('PEPapers Dienstanweisung Druck fehlgeschlagen:', err);
            window.alert('Druckvorschau konnte nicht erstellt werden.');
        } finally {
            setIsPdfDownloading(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (isActionLocked || isPdfDownloading) return;
        const html = getDocumentHtml();
        if (!html.trim()) {
            window.alert('Dokumentinhalt fehlt — bitte Vorschau prüfen.');
            return;
        }
        setIsPdfDownloading(true);
        try {
            await downloadHtmlAsPdf(buildPdfOptions(html));
        } catch (err: any) {
            console.error('PEPapers Dienstanweisung PDF-Download fehlgeschlagen:', err);
            window.alert(
                'PDF konnte nicht heruntergeladen werden: ' +
                    (String(err?.message || '').match(/setEnd|Range|offset/i)
                        ? 'PDF-Erstellung ist fehlgeschlagen. Bitte Seite neu laden und erneut versuchen.'
                        : err?.message || 'Unbekannter Fehler')
            );
        } finally {
            setIsPdfDownloading(false);
        }
    };

    const handleArchive = async (asDraft = false) => {
        if (isSaving) return;
        if (!asDraft && isActionLocked) return;

        setIsSaving(true);
        setArchiveFeedback('idle');
        syncStore();

        const htmlContent = currentStep === 2 ? getDocumentHtml() : '';

        try {
            const id = await savePePapersArchiveDoc({
                id: archiveDocId,
                documentType: 'dienstanweisung-social-media',
                status: asDraft ? 'entwurf' : 'final',
                salonName: betriebData.firmenname || 'Unbekannter Salon',
                mitarbeiterName: empName,
                confirmedDisclaimer: asDraft ? disclaimerAccepted : true,
                currentStep,
                payload: {
                    betriebData,
                    selectedEmployeeId,
                    effectiveDate,
                    customRulesText,
                    socialMedia,
                    equipment,
                    documentTitle: documentHeaderTitle,
                    archiveLabel: documentArchiveLabel,
                    generatedHtml: htmlContent,
                },
            });
            setArchiveDocId(id);
            setArchiveFeedback(asDraft ? 'draft' : 'success');
            onArchived?.();
            if (asDraft) {
                setTimeout(() => {
                    onClose();
                }, 600);
            } else {
                setTimeout(() => setArchiveFeedback('idle'), 3000);
            }
        } catch (error) {
            console.error('PEPapers Dienstanweisung Archivierung fehlgeschlagen:', error);
            setArchiveFeedback('error');
        } finally {
            setIsSaving(false);
        }
    };

    const renderBetriebField = (field: keyof BetriebFormData, label: string) => {
        const fieldId = `betrieb_${field}`;
        return (
            <div>
                <label className={LABEL_CLASS}>{label}</label>
                <input
                    type="text"
                    value={betriebData[field]}
                    onChange={(e) => updateBetriebField(field, e.target.value)}
                    className={getInputClass(INPUT_CLASS, fieldId)}
                    autoComplete="organization"
                />
                {isFieldInvalid(fieldId) ? (
                    <p className="text-[10px] text-red-400 mt-1">Dieses Feld ist erforderlich</p>
                ) : null}
            </div>
        );
    };

    const renderSignatureTable = () => (
        <div className="av-table-wrap">
            <table>
                <thead>
                    <tr>
                        <th style={{ width: '10%' }}>Lfd. Nr.</th>
                        <th style={{ width: '42%' }}>Name des Mitarbeiters (Druckbuchstaben)</th>
                        <th style={{ width: '18%' }}>Datum</th>
                        <th style={{ width: '30%' }}>Unterschrift</th>
                    </tr>
                </thead>
                <tbody>
                    {Array.from({ length: signatureRowCount }, (_, index) => (
                        <tr key={index} className="signature-row">
                            <td>{index + 1}</td>
                            <td>&nbsp;</td>
                            <td>&nbsp;</td>
                            <td>&nbsp;</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const renderLetterhead = () => (
        <div className="pdf-letterhead text-center">
            <h1>{documentHeaderTitle}</h1>
            <h2>PreisExpert · PEPapers · {documentSubtitle}</h2>
        </div>
    );

    const renderSignaturePageBody = () =>
        isTeamScope ? (
            <div className="av-doc-section">
                <h3>Unterschriftenliste – zur Kenntnis genommen und verbindlich anerkannt</h3>
                <p className={DOC_P_CLASS}>
                    Bitte Name in Druckbuchstaben eintragen, Datum setzen und unterschreiben.
                </p>
                {renderSignatureTable()}
            </div>
        ) : (
            <div className="av-sig-grid">
                <div className="av-sig-col">
                    <p className="av-sig-label">Ort, Datum: {ortDatum}</p>
                    <div className="av-sig-line" />
                    <p className="av-sig-name">
                        {betriebData.inhaber || betriebData.firmenname || 'Saloninhaber'}
                    </p>
                    <p className="av-sig-label">Saloninhaber / Arbeitgeber</p>
                </div>
                <div className="av-sig-col">
                    <p className="av-sig-label">Ort, Datum: ________________________</p>
                    <div className="av-sig-line" />
                    <p className="av-sig-name">{signatureEmployeeName}</p>
                    <p className="av-sig-label">
                        Mitarbeiter – zur Kenntnis genommen und als verbindlich anerkannt
                    </p>
                </div>
            </div>
        );

    const renderContractDocument = () => (
        <div ref={printContainerRef} id="pepapers-dienstanweisung-print" className="w-full h-auto overflow-visible">
            <style>{DA_CONTRACT_PDF_CSS}</style>

            {/* Blatt 1: nur Text */}
            <div className={`${A4_PREVIEW_PAGE_CLASS} min-h-[297mm]`}>
                {renderLetterhead()}

                <div className="av-meta-grid">
                    <div className="av-meta-col">
                        <p className="av-meta-label">Betrieb / Arbeitgeber</p>
                        <p>
                            <strong>{betriebData.firmenname || '____________'}</strong>
                            {betriebData.strasse ? (
                                <>
                                    <br />
                                    {betriebData.strasse}
                                </>
                            ) : null}
                            {betriebData.plz || betriebData.ort ? (
                                <>
                                    <br />
                                    {[betriebData.plz, betriebData.ort].filter(Boolean).join(' ')}
                                </>
                            ) : null}
                            {betriebData.inhaber ? (
                                <>
                                    <br />
                                    Inhaber: {betriebData.inhaber}
                                </>
                            ) : null}
                        </p>
                    </div>
                    <div className="av-meta-col">
                        <p className="av-meta-label">Geltungsbereich</p>
                        <p>
                            <strong>{empName}</strong>
                            <span className="av-meta-date">Gültig ab: {formatDateGerman(effectiveDate)}</span>
                        </p>
                    </div>
                </div>

                <div className="av-doc-section">
                    <h3>Präambel</h3>
                    <p className={DOC_P_CLASS}>
                        Diese Dienstanweisung dient dem Schutz der Salonstandards, der Betriebsmittel sowie der
                        personenbezogenen Daten unserer Kunden. Sie gilt verbindlich für den genannten Empfängerkreis ab
                        dem angegebenen Datum.
                    </p>
                </div>

                {documentSections.map((section) => (
                    <div key={section.title} className="av-doc-section">
                        <h3>{section.title}</h3>
                        <p className={DOC_P_CLASS}>{section.content}</p>
                    </div>
                ))}
            </div>

            {/* Blatt 2: Unterschriften – harter PDF/Druck-Split */}
            <div className={`print-break pepapers-print-break ${A4_PREVIEW_PAGE_CLASS} min-h-[297mm]`}>
                {renderLetterhead()}
                {renderSignaturePageBody()}
            </div>
        </div>
    );

    const modalWidthClass = currentStep === 2 ? 'max-w-4xl' : 'max-w-3xl';

    return createPortal(
        <>
            <div
                className="fixed inset-0 bg-stone-900/70 dark:bg-black/90 z-[100000] backdrop-blur-md print:hidden"
                onClick={handleClose}
                aria-hidden="true"
            />
            <div className="fixed inset-x-0 top-[8.5rem] md:top-36 bottom-0 z-[100001] flex items-start justify-center overflow-hidden p-4 pb-8 font-sans print:inset-0 print:top-0 print:overflow-visible print:p-0">
                <div
                    className={`bg-pe-card dark:bg-[#111] rounded-2xl shadow-[0_0_40px_rgba(230,194,153,0.1)] border border-[#E6C299]/30 w-full ${modalWidthClass} max-h-[calc(100vh-9.5rem)] md:max-h-[calc(100vh-10rem)] flex min-h-0 flex-col justify-between overflow-hidden relative transition-all duration-200 my-2 print:shadow-none print:border-0 print:max-w-none print:max-h-none print:rounded-none print:my-0 print:bg-white`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        type="button"
                        onClick={handleClose}
                        className="absolute top-4 right-4 text-pe-muted dark:text-slate-400 hover:text-pe-text dark:hover:text-white bg-pe-elevated dark:bg-[#1a1a1a] hover:bg-pe-hover dark:hover:bg-[#222] rounded-lg p-2 transition-colors border border-pe-border-strong dark:border-[#333] z-[60] print:hidden"
                        aria-label="Schließen"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    <div className="bg-pe-elevated dark:bg-[#111622] z-50 shrink-0 print:hidden border-b border-pe-border dark:border-[#222]">
                        <div className="px-6 pt-10 pb-4">
                            <h3 className="text-lg font-bold text-pe-accent-text dark:text-[#E6C299] text-center uppercase tracking-widest pr-8 leading-snug">
                                {documentArchiveLabel}
                            </h3>
                            <p className="text-center text-xs text-pe-muted dark:text-slate-400 uppercase tracking-widest mt-2">
                                Schritt {currentStep} von 2: {STEP_LABELS[currentStep]}
                            </p>
                        </div>
                    </div>

                    <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 custom-scrollbar print:p-0 print:overflow-visible">
                        {currentStep === 1 && (
                            <div className="space-y-5">
                                <div className="flex items-center justify-between gap-3">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 border-b border-pe-border dark:border-[#222] pb-2 flex-1">
                                        Betriebsdaten &amp; Regeln
                                    </p>
                                    <button
                                        type="button"
                                        onClick={handleReset}
                                        className="rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] p-2 text-pe-muted dark:text-slate-400 hover:text-pe-accent-text dark:hover:text-[#E6C299]"
                                        aria-label="Zurücksetzen"
                                        title="Konfiguration zurücksetzen"
                                    >
                                        <RotateCcw className="h-4 w-4" />
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    {renderBetriebField('firmenname', 'Firma / Salonname')}
                                    {renderBetriebField('inhaber', 'Inhaber / Unternehmer')}
                                    {renderBetriebField('strasse', 'Straße & Hausnummer')}
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        {renderBetriebField('plz', 'PLZ')}
                                        <div className="sm:col-span-2">{renderBetriebField('ort', 'Ort')}</div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <label className="block">
                                        <span className={LABEL_CLASS}>Mitarbeiter</span>
                                        <select
                                            value={selectedEmployeeId}
                                            onChange={(e) => setSelectedEmployeeId(e.target.value)}
                                            className={INPUT_CLASS}
                                        >
                                            <option value="all">Gesamtes Team (Alle Mitarbeiter)</option>
                                            {employees.map((emp: { id: string; name?: string }) => (
                                                <option key={emp.id} value={emp.id}>
                                                    {emp.name || 'Ohne Name'}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="block">
                                        <span className={LABEL_CLASS}>Gültig ab</span>
                                        <input
                                            type="date"
                                            value={effectiveDate}
                                            onChange={(e) => {
                                                setEffectiveDate(e.target.value);
                                                clearFieldError('effectiveDate');
                                            }}
                                            className={getInputClass(INPUT_CLASS, 'effectiveDate')}
                                        />
                                        {isFieldInvalid('effectiveDate') ? (
                                            <p className="text-[10px] text-red-400 mt-1">Dieses Feld ist erforderlich</p>
                                        ) : null}
                                    </label>
                                </div>

                                <div className="space-y-2">
                                    <p className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-pe-muted dark:text-slate-500">
                                        <ShieldCheck className="h-3.5 w-3.5 text-pe-accent-text dark:text-[#E6C299]" />
                                        Social Media &amp; Smartphone
                                    </p>
                                    {SOCIAL_TOGGLES.map((item) => (
                                        <ToggleRow
                                            key={item.key}
                                            title={item.title}
                                            sub={item.sub}
                                            checked={socialMedia[item.key]}
                                            onChange={(value) =>
                                                setSocialMedia((prev) => ({ ...prev, [item.key]: value }))
                                            }
                                        />
                                    ))}
                                </div>

                                <div className="space-y-2">
                                    <p className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-pe-muted dark:text-slate-500">
                                        <Scissors className="h-3.5 w-3.5 text-pe-accent-text dark:text-[#E6C299]" />
                                        Equipment &amp; Hardware
                                    </p>
                                    {EQUIPMENT_TOGGLES.map((item) => (
                                        <ToggleRow
                                            key={item.key}
                                            title={item.title}
                                            sub={item.sub}
                                            checked={equipment[item.key]}
                                            onChange={(value) =>
                                                setEquipment((prev) => ({ ...prev, [item.key]: value }))
                                            }
                                        />
                                    ))}
                                </div>

                                <label className="block">
                                    <span className={`${LABEL_CLASS} flex items-center gap-2`}>
                                        <FileText className="h-3.5 w-3.5" />
                                        Individuelle Zusatzabsprachen (optional)
                                    </span>
                                    <textarea
                                        value={customRulesText}
                                        onChange={(e) => setCustomRulesText(e.target.value)}
                                        rows={4}
                                        placeholder="Hier zusätzliche salon-spezifische Regeln eintragen..."
                                        className={`${INPUT_CLASS} resize-y`}
                                    />
                                </label>

                                {salonAddressLine ? (
                                    <p className="text-[10px] text-pe-muted dark:text-slate-500">
                                        Adresse in der Vorschau: {salonAddressLine}
                                    </p>
                                ) : null}
                            </div>
                        )}

                        {currentStep === 2 && (
                            <div className="w-full min-h-0 pr-2 mb-6">{renderContractDocument()}</div>
                        )}
                    </div>

                    {currentStep === 2 && (
                        <PePapersDisclaimerGate
                            accepted={disclaimerAccepted}
                            onAcceptedChange={setDisclaimerAccepted}
                        />
                    )}

                    <div className="px-6 py-4 border-t border-pe-border dark:border-[#222] shrink-0 print:hidden">
                        <div className="flex flex-wrap gap-3">
                            {currentStep > 1 && (
                                <button
                                    type="button"
                                    onClick={handleZurueck}
                                    className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors"
                                >
                                    Zurück
                                </button>
                            )}

                            {currentStep === 1 ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(true)}
                                        disabled={isSaving}
                                        className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors disabled:opacity-50"
                                    >
                                        {isSaving && archiveFeedback !== 'draft' ? 'Speichert...' : 'Später fortsetzen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleWeiter}
                                        className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-[#E6C299] hover:bg-[#d9b183] dark:hover:bg-white text-black transition-colors"
                                    >
                                        Dokument erstellen
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(true)}
                                        disabled={isSaving}
                                        className="flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors disabled:opacity-50"
                                    >
                                        {archiveFeedback === 'draft' ? 'Gespeichert! ✓' : 'Später fortsetzen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(false)}
                                        disabled={isActionLocked || isSaving}
                                        className={getActionButtonClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-pe-card dark:bg-[#15171e] hover:bg-pe-hover dark:hover:bg-[#1a1d24] border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299] transition-colors'
                                        )}
                                    >
                                        {isSaving && archiveFeedback !== 'draft'
                                            ? 'Speichert...'
                                            : archiveFeedback === 'success'
                                              ? 'Gespeichert! ✓'
                                              : 'Im Archiv speichern'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleDownloadPdf()}
                                        disabled={isActionLocked || isPdfDownloading}
                                        className={getActionButtonClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-gradient-to-b from-[#E6C299] to-[#b48a56] hover:from-[#f0d3b3] hover:to-[#cfa674] text-black transition-colors'
                                        )}
                                    >
                                        {isPdfDownloading ? 'PDF wird erstellt...' : 'Als PDF herunterladen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handlePrintIsolated()}
                                        disabled={isActionLocked || isPdfDownloading}
                                        className={getActionButtonClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299] transition-colors'
                                        )}
                                    >
                                        Ausdrucken
                                    </button>
                                </>
                            )}
                        </div>
                        {currentStep === 2 && archiveFeedback === 'error' && (
                            <p className="text-[10px] text-red-400 mt-2 text-center">
                                Archivierung fehlgeschlagen. Bitte erneut versuchen.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default PEPapersInstructionModal;
